(() => {
  'use strict';
  if (window.drboTrustpilotLive) return;
  window.drboTrustpilotLive = true;
  const selector = '[data-drbo-trustpilot]';
  const observed = new WeakSet();
  let bootstrap;

  function sizeWidget(element) {
    const summary = element.dataset.ratingView === 'summary';
    const height = summary ? (element.clientWidth >= 480 ? 28 : 48) : (element.clientWidth >= 280 ? 24 : 48);
    element.style.height = `${height}px`;
    element.dataset.styleHeight = `${height}px`;
    const frame = element.querySelector('iframe');
    if (frame) frame.style.height = `${height}px`;
  }

  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(entries => {
    entries.forEach(entry => sizeWidget(entry.target));
  }) : null;

  function getBootstrap() {
    if (window.Trustpilot?.loadFromElement) return Promise.resolve(window.Trustpilot);
    if (bootstrap) return bootstrap;
    bootstrap = new Promise((resolve, reject) => {
      const existing = Array.from(document.scripts).some(script => {
        try {
          const url = new URL(script.src);
          return url.hostname === 'widget.trustpilot.com' && url.pathname.includes('/bootstrap/');
        } catch (_) { return false; }
      });
      if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
        script.async = true;
        document.head.append(script);
      }
      const deadline = Date.now() + 15000;
      const poll = () => {
        if (window.Trustpilot?.loadFromElement) resolve(window.Trustpilot);
        else if (Date.now() >= deadline) reject(new Error('Trustpilot bootstrap unavailable'));
        else window.setTimeout(poll, 100);
      };
      poll();
    });
    return bootstrap;
  }

  async function initialize(element) {
    if (!element.isConnected || element.dataset.tpState) return;
    element.dataset.tpState = 'loading';
    try {
      const api = await getBootstrap();
      if (!element.isConnected) return;
      element.dataset.locale ||= document.documentElement.lang.startsWith('nl') ? 'nl-NL' : 'de-DE';
      const summary = element.dataset.ratingView === 'summary';
      element.dataset.templateId = summary ? '5419b6ffb0d04a076446a9af' : '5419b637fa0340045cd0c936';
      element.dataset.businessunitId = '6396f498f1b97c8d18a7672f';
      sizeWidget(element);
      element.dataset.styleWidth = '100%';
      if (!['light', 'dark'].includes(element.dataset.theme)) {
        const rgb = getComputedStyle(element).color.match(/[\d.]+/g)?.slice(0, 3).map(Number);
        const brightness = rgb?.length === 3 ? rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114 : 0;
        element.dataset.theme = brightness > 150 ? 'dark' : 'light';
      }
      element.classList.add('trustpilot-widget');
      if (!element.querySelector('iframe')) api.loadFromElement(element);
      resizeObserver?.observe(element);
      element.dataset.tpState = 'initialized';
    } catch (_) {
      // Leave the neutral company-profile link intact when the provider is unavailable.
      element.dataset.tpState = 'unavailable';
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      initialize(entry.target);
    }
  }, { rootMargin: '200px' }) : null;

  function scan(root) {
    const elements = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) elements.unshift(root);
    for (const element of elements) {
      if (observed.has(element)) continue;
      observed.add(element);
      if (observer) observer.observe(element);
      else initialize(element);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  // Recover if the official bootstrap finishes after the initial timeout.
  document.addEventListener('load', event => {
    const script = event.target;
    if (script.tagName !== 'SCRIPT' || !script.src || !window.Trustpilot?.loadFromElement) return;
    const url = new URL(script.src);
    if (url.hostname !== 'widget.trustpilot.com' || !url.pathname.includes('/bootstrap/')) return;
    document.querySelectorAll(`${selector}[data-tp-state="unavailable"]`).forEach(element => {
      delete element.dataset.tpState;
      initialize(element);
    });
  }, true);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(selector).forEach(element => {
      observer?.unobserve(element);
      resizeObserver?.unobserve(element);
    });
  });
})();
